import { Box, CircularProgress, Grid2, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useGetCurrentCampaignQuery,
  useGetUserQuery,
  useRegenerateSuggestedTitlesMutation,
} from "../api";
import {
  PenIcon,
  StarsOutlineIcon,
  ThreePersonIcon,
} from "../components/images";
import {
  CustomizeDocumentationBlock,
  DeleteProductBtn,
  ProductDetailsModal,
  GoToActionBlock,
  ProductNotes,
} from "../components/products";
import {
  ExpandedTextBlock,
  FileListItem,
  GradientButton,
  HgInsightsBlock,
  LinkListItem,
  NavigationBreadcrumbs,
  ReuseButton,
  ReuseTooltip,
} from "../components/ui";
import { CustomButton } from "../components/ui/CustomButton";
import { CommitteeRoleItem } from "../components/products/CommitteeRoleItem";
import { Statistics } from "../components/products/Statistics";
import { toast } from "react-toastify";
import { CustomToast } from "../components/ui/CustomToast";
import { getErrorMessage } from "../utils/getErrorMessage";
import { RestoreCampaignBtn } from "../components/ui/buttons";
import { AppRoutes, ButtonsColorType, CampaignPurpose } from "../types/enums";
import { QuestionMarkInCircleIcon } from "../components/images/QuestionMarkInCircleIcon";
import { useAppSelector, useNotFoundRedirect } from "../hooks";
import { SixSensBlock } from "../components/products/CustomizeDocumentationBlock/SixSensBlock";
import { AnalyzeJobTitlesModal } from "../components/ui/modal";
import { selectPermissions } from "../features/permissionsSlice";
import { RolesList } from "../components/products/RolesList";
import { AnalyticsEvent } from "../types/events";
import { trackEvent } from "../utils/analyticsService";

const ProductPage = () => {
  const {
    isAllowedEditActionsTargetTitles,
    isAllowedEditResyncData,
    isAllowedEditCreateNewCampaign,
  } = useAppSelector(selectPermissions);

  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const { campaign_id } = useParams();

  const { data: user } = useGetUserQuery();

  const {
    data,
    refetch,
    error: currentCampaignError,
  } = useGetCurrentCampaignQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  useNotFoundRedirect({ error: currentCampaignError });

  const [mutate, { isError, isLoading, error }] =
    useRegenerateSuggestedTitlesMutation();

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          title="Failed to generate suggested titles."
          text={
            getErrorMessage(error).globalError ||
            "Something went wrong. Please try again"
          }
        />,
      );
    }
  }, [isError, error]);

  if (!data) return null;

  const {
    company_name,
    product_name,
    product_abstract,
    key_roles,
    source_pdf_names,
    source_url,
    notes,
    notes_updated_at,
    total_accounts_analyzed,
    campaign_purpose,
  } = data;

  const isDemo = [CampaignPurpose.sales, CampaignPurpose.demo].includes(
    campaign_purpose,
  );

  const isClientView = !!user?.client_view;

  const breadcrumbs = [
    {
      title: product_name,
    },
  ];

  const submitChanges = () => {
    setIsEditModal(false);
    refetch();
  };

  return (
    <Stack sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4 }} gap="40px">
      <Stack gap="24px" position="sticky">
        <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
        <Grid2 container spacing={4}>
          <Grid2 size={{ xs: 12, md: 7.7 }}>
            <Stack gap="16px" sx={{ pb: 6 }}>
              <Stack direction="row" alignItems="center" gap="16px">
                <Typography variant="h4" color="custom.textDarkBlue">
                  {product_name}
                </Typography>
                <Box
                  sx={{
                    minWidth: "8px",
                    minHeight: "8px",
                    backgroundColor: "#4C545B",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="h4" color="#797F84">
                  {company_name}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={"12px"}
              >
                <Stack flexDirection="row" gap="8px">
                  <Typography
                    sx={{
                      fontSize: "18px",
                      lineHeight: "28px",
                      fontWeight: 500,
                      color: "#1F2932",
                    }}
                  >
                    Buying Committee Overview by Role
                  </Typography>
                  <ReuseTooltip
                    text={
                      "Buying committee is a group of people inside the target account who will be directly" +
                      " involved in the decision-making process for a deal involving your product, service," +
                      " or solution."
                    }
                  >
                    <QuestionMarkInCircleIcon />
                  </ReuseTooltip>
                </Stack>

                <Stack flexDirection={"row"} gap={"12px"}>
                  <AnalyzeJobTitlesModal
                    isDisabled={!isAllowedEditActionsTargetTitles}
                    campaign_id={campaign_id!}
                    onSuccessHandler={refetch}
                  />
                  <ReuseTooltip
                    text={
                      "Get target profile suggestions based on the job titles, segmented by the role" +
                      " in the buying committee. Confirm the suggestion to add it to the campaign."
                    }
                  >
                    <ReuseButton
                      onClick={() => {
                        mutate(campaign_id!)
                          .unwrap()
                          .then((result) => {
                            trackEvent(AnalyticsEvent.SuggestTitlesClicked, {
                              campaign_id: campaign_id!,
                              result_count: result.result_count,
                            });
                          });
                      }}
                      buttonColor={ButtonsColorType.dark_orange}
                      disabled={isLoading || !isAllowedEditActionsTargetTitles}
                      startIcon={
                        isLoading ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: "inherit" }}
                          />
                        ) : (
                          <StarsOutlineIcon />
                        )
                      }
                    >
                      Suggest Titles
                    </ReuseButton>
                  </ReuseTooltip>
                </Stack>
              </Stack>
              {total_accounts_analyzed === 0 && (
                <GoToActionBlock
                  title="Customize Targeting & Launch the Discovery"
                  texts={[
                    "Utilize title suggestions and segmentation to specify targeting. Customize buying committee" +
                      " composition using role switches. When ready, proceed to buying committee discovery in target" +
                      " accounts.",
                  ]}
                  btn={
                    <Link to={`${AppRoutes.ACCOUNTS_LIST}/${campaign_id}`}>
                      <GradientButton style={{ width: "100%", height: "36px" }}>
                        Proceed to Accounts
                      </GradientButton>
                    </Link>
                  }
                />
              )}

              <Stack
                sx={{
                  borderRadius: "6px",
                  backgroundColor: "#F7F7F7",
                  padding: "12px 16px 16px",
                  gap: "8px",
                }}
              >
                <Stack flexDirection={"row"} gap="8px" alignItems={"center"}>
                  <ThreePersonIcon />
                  <Typography
                    variant="subtitle1"
                    color="custom.textDarkBlue"
                    sx={{ fontWeight: 500 }}
                  >
                    Functional roles
                  </Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  Tied to job function. These roles are always present in the
                  buying committee because of the individual's organisational
                  responsibilities — regardless of deal size or product.
                </Typography>
              </Stack>

              <RolesList campaign_id={campaign_id!} key_roles={key_roles} />
            </Stack>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4.3 }}>
            <Stack
              gap="36px"
              position="sticky"
              sx={{
                padding: "12px",
                top: isDemo ? 110 : 74,
                height: `calc(100vh - 74px)`,
                scrollbarWidth: "none",
                overflow: "auto",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                pb: 6,
              }}
            >
              <Statistics data={data} campaign_id={campaign_id!} />
              <SixSensBlock campaign_id={campaign_id!} />
              <CustomizeDocumentationBlock campaign_id={campaign_id!} />
              <Stack gap="24px">
                <Stack gap="8px">
                  <Typography
                    variant="subtitle1"
                    color="custom.textDarkBlue"
                    sx={{ fontWeight: 500 }}
                  >
                    Product, Solution or Service Description
                  </Typography>
                  <ExpandedTextBlock text={product_abstract} />
                </Stack>

                {(source_url ||
                  (source_pdf_names && source_pdf_names.length > 0)) && (
                  <Stack gap="8px">
                    <Typography
                      variant="subtitle1"
                      color="custom.textDarkBlue"
                      sx={{ fontWeight: 500 }}
                    >
                      Sources
                    </Typography>
                    <Stack justifyContent="center" gap="10px">
                      {source_url && <LinkListItem url={source_url} />}

                      {source_pdf_names &&
                        source_pdf_names.map((name) => (
                          <FileListItem key={name} fileName={name} />
                        ))}
                    </Stack>
                  </Stack>
                )}

                <ProductNotes
                  campaign_id={campaign_id!}
                  notes={notes ?? ""}
                  last_updated={notes_updated_at}
                />

                <HgInsightsBlock />

                {(isAllowedEditResyncData ||
                  isAllowedEditCreateNewCampaign) && (
                  <Stack
                    direction="row"
                    gap="8px"
                    sx={{
                      flexWrap: {
                        "@media (max-width:1510px)": {
                          flexWrap: "wrap",
                        },
                        "@media (min-width:1511px)": {
                          flexWrap: "nowrap",
                        },
                      },
                    }}
                  >
                    {isAllowedEditResyncData && !isClientView && (
                      <ReuseTooltip
                        placement="top"
                        hideChildren={false}
                        style={{ maxWidth: "100%", width: "100%" }}
                        text={
                          isDemo
                            ? "This feature is not available in the demo campaign"
                            : ""
                        }
                      >
                        <CustomButton
                          disabled={isDemo}
                          onClick={() => setIsEditModal(true)}
                          variant="outlined"
                          customColor="#FFFFFF"
                          textColor="#1D3E70"
                          fullWidth
                          type="button"
                          startIcon={<PenIcon />}
                          sx={{ height: "36px", borderColor: "#1D3E70" }}
                        >
                          Edit Data
                        </CustomButton>
                      </ReuseTooltip>
                    )}

                    {isAllowedEditCreateNewCampaign &&
                      (isDemo ? (
                        <RestoreCampaignBtn campaign_id={campaign_id!} />
                      ) : (
                        <DeleteProductBtn campaign_id={campaign_id!} />
                      ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Grid2>
        </Grid2>
      </Stack>

      {isEditModal && (
        <ProductDetailsModal
          initProductUrl={""}
          campaign_id={campaign_id!}
          isOpen={isEditModal}
          onClose={() => setIsEditModal(false)}
          onSubmit={submitChanges}
          company_name={company_name}
          product_name={product_name}
          product_abstract={product_abstract}
        />
      )}
    </Stack>
  );
};

export default ProductPage;
